<template>
	<div
		class="help-hero-composer"
		data-testid="help-hero-composer"
	>
		<!--
			The Vue twin of the platform hub's HomeComposer (AB#27211). Deliberately a hand-rolled
			field rather than a `v-textarea`: Vuetify wraps its inputs in a fieldset with its own
			border, legend notch and density rules, which no amount of overriding brings level with
			the React InputGroup. This mirrors that primitive's anatomy instead, so the two composers
			are the same object: one boundary, a leading ai-assistant glyph, an evenly inset Send,
			and the field growing to a cap. Keep them in step.
		-->
		<div
			class="help-hero-composer__group"
			role="group"
			@click="focus"
		>
			<span class="help-hero-composer__addon">
				<ai-assistant-icon
					:size="18"
					class="help-hero-composer__glyph"
					data-testid="help-hero-composer-glyph"
				/>
			</span>

			<textarea
				ref="input"
				v-model="text"
				:aria-label="ariaLabel"
				data-testid="help-hero-composer-input"
				class="help-hero-composer__field"
				rows="1"
				:maxlength="MAX_MESSAGE_CHARS"
				:disabled="disabled"
				:placeholder="placeholder"
				@keydown="handleKeydown"
				@input="resize"
			/>

			<span class="help-hero-composer__addon help-hero-composer__addon--end">
				<button
					type="button"
					data-testid="help-hero-composer-send"
					aria-label="Send"
					:disabled="isSendDisabled"
					class="help-hero-composer__send"
					@click.stop="handleSend"
				>
					<LucideIcon
						name="ArrowUp"
						:size="16"
					/>
				</button>
			</span>
		</div>

		<!-- Under the field, as the panel composer shows it; see help-composer.vue for the reasoning. -->
		<p
			v-if="counter"
			data-testid="help-hero-composer-char-count"
			:class="['help-hero-composer__count', { 'help-hero-composer__count--limit': counter.atLimit }]"
		>
			{{ counter.text }}{{ counter.atLimit ? ' · Limit reached' : '' }}
		</p>
		<span
			role="status"
			data-testid="help-hero-composer-char-count-status"
			class="d-sr-only"
		>
			{{ counter ? counter.status : '' }}
		</span>

		<div
			v-if="questions.length > 0 || $slots.trailing"
			class="help-hero-composer__chips"
		>
			<help-suggestion-chips
				:questions="questions"
				:disabled="disabled"
				test-id-prefix="help-hero-composer-suggestion"
				@select="handleSelect"
			/>
			<slot name="trailing" />
		</div>
	</div>
</template>

<script>
import { LucideIcon } from '@syrenis/cassie-material-design'
import AiAssistantIcon from '../../../../../shared/components/ai-assistant-icon.vue'
import HelpSuggestionChips from './help-suggestion-chips.vue'
import { COMPOSER_MAX_HEIGHT_PX, MAX_MESSAGE_CHARS, composerCounter } from './utils/chat-core.js'

export default {
	name: 'help-hero-composer',
	components: {
		AiAssistantIcon,
		LucideIcon,
		HelpSuggestionChips
	},
	props: {
		/** Suggestion chips rendered beneath the field. Three fit one row on a wide splash page. */
		questions: {
			type: Array,
			default: () => []
		},
		placeholder: {
			type: String,
			default: 'Ask anything about Syrenis…'
		},
		ariaLabel: {
			type: String,
			default: 'Ask anything about Syrenis'
		},
		disabled: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			text: '',
			MAX_MESSAGE_CHARS
		}
	},
	computed: {
		isSendDisabled () {
			return this.text.trim().length === 0 || this.disabled
		},
		counter () {
			return composerCounter(this.text.length)
		}
	},
	mounted () {
		this.resize()
	},
	methods: {
		/** Lets a host put the caret here, which the hero does when a conversation closes. */
		focus () {
			// preventScroll: the hero sits below the page heading, and the default focus behaviour
			// scrolls the field into view, which yanks the page mid-transition (AB#27211).
			this.$refs.input?.focus({ preventScroll: true })
		},
		/** Grows with the content to the cap, then scrolls. Reset first so it can shrink again. The
		 * cap is set here too, so the stylesheet has no second copy of the number to drift. */
		resize () {
			const field = this.$refs.input
			if (!field) return
			field.style.maxHeight = `${COMPOSER_MAX_HEIGHT_PX}px`
			field.style.height = 'auto'
			const next = field.scrollHeight
			if (next <= 0) return
			field.style.height = `${Math.min(next, COMPOSER_MAX_HEIGHT_PX)}px`
			field.style.overflowY = next > COMPOSER_MAX_HEIGHT_PX ? 'auto' : 'hidden'
		},
		handleKeydown (event) {
			// isComposing: committing an IME candidate fires Enter, and without this the draft sends
			// mid-composition. The React HomeComposer guards the same way.
			if (event.isComposing) return
			if (event.key === 'Enter' && !event.shiftKey) {
				event.preventDefault()
				this.submit(this.text)
			}
		},
		handleSend () {
			this.submit(this.text)
		},
		handleSelect ({ text }) {
			this.submit(text)
		},
		submit (value) {
			const trimmed = (value ?? '').trim()
			if (!trimmed || this.disabled) return
			this.$emit('send', { text: trimmed })
			this.text = ''
			this.$nextTick(this.resize)
		}
	}
}
</script>

<style lang="scss" scoped>
.help-hero-composer {
	display: flex;
	flex-direction: column;
	gap: 12px;

	// One boundary, 12px of padding all round, so Send sits evenly inset on all four sides.
	// Centred, not bottom-pinned like the panel composer: with the glyph and Send framing a wide
	// field, pinning both to the last line read as lopsided once the draft grew.
	&__group {
		display: flex;
		align-items: center;
		width: 100%;
		padding: 12px 0;
		cursor: text;
		background-color: var(--card, #FFFFFF);
		border: 1px solid var(--input, #E0E2E5);
		border-radius: var(--radius, 8px);
		transition: border-color 0.15s ease, box-shadow 0.15s ease;

		&:focus-within {
			border-color: var(--primary, #406A94);
			box-shadow: 0 0 0 3px rgb(64 106 148 / 25%);
		}
	}

	&__addon {
		display: flex;
		flex: none;
		align-items: center;
		padding-left: 12px;

		// 12px on the field side as well, so the scrollbar of a tall draft stays off the button.
		&--end {
			padding-right: 12px;
			padding-left: 12px;
		}
	}

	// A flex child of the 12px column; pulled up so it sits 4px under the field as in the panel.
	&__count {
		margin: -8px 0 0;
		font-size: 0.75rem;
		font-variant-numeric: tabular-nums;
		color: var(--muted-foreground, #666E7A);
		text-align: right;

		&--limit {
			font-weight: 500;
			color: var(--foreground, #4A5568);
		}
	}

	&__glyph {
		color: var(--primary, #406A94);
	}

	&__field {
		flex: 1 1 auto;
		min-width: 0;
		min-height: 20px;
		padding: 2px 12px;
		overflow-y: hidden;
		font-family: inherit;
		font-size: 14px;
		line-height: 20px;
		color: var(--foreground, #4A5568);
		resize: none;
		background: none;
		border: none;
		outline: none;

		&::placeholder {
			color: var(--muted-foreground, #666E7A);
		}

		&:disabled {
			cursor: not-allowed;
			opacity: 0.5;
		}
	}

	// 32px square at the token radius, matching React's filled Send.
	&__send {
		display: flex;
		flex: none;
		align-items: center;
		justify-content: center;
		width: 32px;
		height: 32px;
		color: var(--primary-foreground, #FFFFFF);
		cursor: pointer;
		background-color: var(--primary, #406A94);
		border: none;
		border-radius: var(--radius, 8px);

		&:disabled {
			cursor: not-allowed;
			opacity: 0.5;
		}

		&:focus:not(:focus-visible) {
			outline: none;
		}

		&:focus-visible {
			outline: 2px solid var(--primary, #406A94);
			outline-offset: 2px;
		}
	}

	&__chips {
		display: flex;
		flex-wrap: wrap;
		gap: 8px 24px;
		align-items: center;
		justify-content: space-between;
	}
}
</style>
